import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarDays, Loader2, Trash2, Wand2, ImageIcon, RefreshCw, Paperclip, Users, Pencil, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { PostMediaUpload } from './PostMediaUpload';
import { Textarea } from '../components/ui/textarea';

const IMAGE_MODELS = [
  { id: 'nano-banana', label: 'Nano Banana', desc: 'Gemini · default · best for photoreal' },
  { id: 'gpt-image-1', label: 'GPT Image 1', desc: 'OpenAI · best for marketing graphics' },
  { id: 'dall-e-3', label: 'DALL·E 3', desc: 'OpenAI · creative · widely supported' },
];

/**
 * Shared dialog used to schedule (or reschedule) a single post.
 * Props:
 *   post: object with at least { id, scheduled_for }
 *   open: boolean
 *   onClose: fn
 *   onScheduled: fn(updatedPost)
 */
export const SchedulePostDialog = ({ post, open, onClose, onScheduled }) => {
  const { api } = useAuth();
  const [date, setDate] = useState(() => new Date());
  const [time, setTime] = useState('09:00');
  const [saving, setSaving] = useState(false);
  const [unscheduling, setUnscheduling] = useState(false);
  // Image generation state
  const [imageModel, setImageModel] = useState('nano-banana');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  // Track local copy of post (so media changes are visible immediately)
  const [livePost, setLivePost] = useState(post);
  // Multi-page targets (Phase 24)
  const [targets, setTargets] = useState([]); // available
  const [selectedKeys, setSelectedKeys] = useState([]); // selected (platform::account_id)
  const [targetsLoading, setTargetsLoading] = useState(false);
  // Per-target caption overrides (Phase 25). Keyed by `platform::account_id`.
  // If a key isn't present, the base caption is used.
  const [captionOverrides, setCaptionOverrides] = useState({});
  const [expandedTargetKey, setExpandedTargetKey] = useState(null);

  // Stable key per target row
  const keyOf = (t) => `${t.platform}::${t.account_id}`;
  const targetsByPlatform = useMemo(() => {
    const map = {};
    for (const t of targets) {
      (map[t.platform] = map[t.platform] || []).push(t);
    }
    return map;
  }, [targets]);

  useEffect(() => {
    setLivePost(post);
  }, [post]);

  useEffect(() => {
    setGeneratedImage(post?.generated_image || null);
  }, [post]);

  useEffect(() => {
    if (!post) return;
    if (post.scheduled_for) {
      const dt = new Date(post.scheduled_for);
      setDate(dt);
      const hh = String(dt.getHours()).padStart(2, '0');
      const mm = String(dt.getMinutes()).padStart(2, '0');
      setTime(`${hh}:${mm}`);
    } else {
      setDate(new Date());
      setTime('09:00');
    }
  }, [post]);

  // Fetch targets when dialog opens, and pre-select the post's current platform+target if any
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTargetsLoading(true);
    api.get('/connections/targets').then((res) => {
      if (cancelled) return;
      const list = res.data?.targets || [];
      setTargets(list);
      // Default selection logic:
      //   1. If post already has a `target`, pre-select that exact one
      //   2. Else pre-select the first target whose platform matches post.platform
      //   3. Else leave empty
      let preselected = [];
      if (post?.target?.account_id) {
        const k = `${post.target.platform}::${post.target.account_id}`;
        if (list.some((t) => keyOf(t) === k)) preselected = [k];
      }
      if (preselected.length === 0 && post?.platform) {
        const wanted = String(post.platform).toLowerCase().replace(/\s+/g, '_');
        const match = list.find(
          (t) => t.platform === wanted || t.platform === post.platform.toLowerCase()
        );
        if (match) preselected = [keyOf(match)];
      }
      setSelectedKeys(preselected);
    }).catch(() => {
      // silent — dialog still functional with legacy single-platform schedule
    }).finally(() => {
      // Always clear loading state — even if the effect re-fired in the meantime,
      // having the new run reset it to true is fine.
      setTargetsLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, post?.id]);

  const toggleTarget = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const [generatingVariants, setGeneratingVariants] = useState(false);

  const handleGenerateVariants = async () => {
    const chosen = targets.filter((t) => selectedKeys.includes(keyOf(t)));
    if (chosen.length < 2) {
      toast.error('Select 2+ destinations to generate variants');
      return;
    }
    if (!post?.caption?.trim()) {
      toast.error('Post needs a base caption before tuning per-destination variants');
      return;
    }
    setGeneratingVariants(true);
    try {
      const res = await api.post(`/posts/${post.id}/caption-variants`, { targets: chosen });
      const variants = res.data?.variants || {};
      const count = Object.keys(variants).length;
      if (count === 0) {
        toast.error('AI did not return any variants — try again');
        return;
      }
      setCaptionOverrides((cur) => ({ ...cur, ...variants }));
      // Auto-expand the first target so the user can see what was generated
      const firstKey = Object.keys(variants)[0];
      if (firstKey) setExpandedTargetKey(firstKey);
      toast.success(`Generated ${count} platform-tuned caption${count === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Variant generation failed');
    } finally {
      setGeneratingVariants(false);
    }
  };

  const handleSchedule = async () => {
    if (!date) {
      toast.error('Pick a date');
      return;
    }
    setSaving(true);
    try {
      const [hh, mm] = time.split(':').map(Number);
      const local = new Date(date);
      local.setHours(hh, mm, 0, 0);
      const isoUtc = local.toISOString();

      // Resolve selected target objects
      const chosen = targets.filter((t) => selectedKeys.includes(keyOf(t)));

      if (chosen.length > 1) {
        // Multi-target → backend clones the post per target
        const res = await api.post(`/posts/${post.id}/schedule-multi`, {
          scheduled_for: isoUtc,
          targets: chosen,
          caption_overrides: captionOverrides,
        });
        toast.success(`Scheduled to ${res.data.total_scheduled} target(s)`);
      } else {
        // Single target (or none — fall back to existing behavior)
        if (chosen.length === 1) {
          // Use schedule-multi anyway so the single target is stamped on the post
          await api.post(`/posts/${post.id}/schedule-multi`, {
            scheduled_for: isoUtc,
            targets: chosen,
            caption_overrides: captionOverrides,
          });
        } else {
          await api.post(`/posts/${post.id}/schedule`, { scheduled_for: isoUtc });
        }
        toast.success(post.scheduled_for ? 'Post rescheduled' : 'Post scheduled');
      }
      // Refresh by fetching the (possibly updated) original post
      try {
        const fresh = await api.get(`/posts/${post.id}`);
        onScheduled?.(fresh.data);
      } catch (_) {
        onScheduled?.(post);
      }
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleUnschedule = async () => {
    setUnscheduling(true);
    try {
      const res = await api.post(`/posts/${post.id}/unschedule`);
      toast.success('Post moved back to drafts');
      onScheduled?.(res.data);
      onClose?.();
    } catch (error) {
      toast.error('Failed to unschedule');
    } finally {
      setUnscheduling(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!post?.image_prompt && !post?.caption) {
      toast.error('No prompt available for this post');
      return;
    }
    setGeneratingImage(true);
    try {
      const res = await api.post('/generate/image', {
        prompt: post.image_prompt || post.caption?.slice(0, 200),
        post_id: post.id,
        model: imageModel,
      });
      setGeneratedImage(res.data.image_base64);
      toast.success(`Image generated with ${imageModel}`);
      onScheduled?.({ ...post, generated_image: res.data.image_base64, generated_image_model: imageModel });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image generation failed');
    } finally {
      setGeneratingImage(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-sm sm:max-w-3xl lg:max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="schedule-post-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-indigo-600" />
            {post?.scheduled_for ? 'Reschedule post' : 'Schedule post'}
          </DialogTitle>
          <DialogDescription className="line-clamp-2">
            {post?.post_title || post?.caption?.slice(0, 80) || 'Pick a date and time'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 md:grid-cols-[auto,1fr]">
          {/* Left column: calendar */}
          <div className="rounded-lg border border-zinc-200 p-2 flex justify-center md:justify-start h-fit">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              className="rounded-md"
              data-testid="schedule-calendar"
            />
          </div>

          {/* Right column: time + image + media */}
          <div className="space-y-4 min-w-0">
            <div className="space-y-2">
              <Label htmlFor="schedule-time">Time (your local timezone)</Label>
              <Input
                id="schedule-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="schedule-time-input"
              />
              <p className="text-xs text-zinc-500">
                Saved in UTC. Currently: {date ? date.toLocaleDateString() : '—'} at {time}
              </p>
            </div>

            {/* Multi-page targets */}
            <div className="space-y-2 pt-2 border-t border-zinc-200" data-testid="targets-section">
              <div className="flex items-center justify-between gap-2">
                <Label className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-indigo-600" />
                  Publish to
                  {selectedKeys.length > 0 && (
                    <Badge variant="outline" className="ml-1 bg-indigo-50 text-indigo-700 border-indigo-200">
                      {selectedKeys.length} selected
                    </Badge>
                  )}
                </Label>
                {selectedKeys.length >= 2 && (
                  <button
                    type="button"
                    onClick={handleGenerateVariants}
                    disabled={generatingVariants}
                    className="text-[11px] inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="generate-variants-btn"
                    title="AI tunes the caption per destination (LinkedIn = professional, IG = punchy with emojis, etc.)"
                  >
                    {generatingVariants
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <Wand2 className="w-3 h-3" />}
                    {generatingVariants ? 'Generating…' : 'AI per-page variants'}
                  </button>
                )}
              </div>
              {targetsLoading ? (
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Loading destinations…
                </div>
              ) : Object.keys(targetsByPlatform).length === 0 ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                  No connected platforms — connect an account on /connections first.
                </p>
              ) : (
                <div className="rounded-md border border-zinc-200 max-h-44 overflow-y-auto divide-y divide-zinc-100">
                  {Object.entries(targetsByPlatform).map(([platform, items]) => (
                    <div key={platform} className="p-2">
                      <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1 capitalize">
                        {platform.replace('_', ' ')} · {items.length}
                      </p>
                      <div className="space-y-1">
                        {items.map((t) => {
                          const k = keyOf(t);
                          const checked = selectedKeys.includes(k);
                          const overridden = !!(captionOverrides[k] && captionOverrides[k].trim());
                          const expanded = expandedTargetKey === k;
                          return (
                            <div key={k} className="rounded">
                              <div
                                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-zinc-50 text-xs"
                                data-testid={`target-row-${platform}-${t.account_id}`}
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={() => toggleTarget(k)}
                                  className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                                />
                                <span
                                  className="flex-1 truncate text-zinc-700 cursor-pointer"
                                  onClick={() => toggleTarget(k)}
                                >
                                  {t.account_name}
                                </span>
                                {overridden && (
                                  <span className="text-[9px] text-amber-700 bg-amber-50 px-1 rounded" title="This destination uses a custom caption">
                                    custom
                                  </span>
                                )}
                                {t.kind === 'org' && (
                                  <span className="text-[9px] text-sky-700 bg-sky-50 px-1 rounded">Company</span>
                                )}
                                {t.kind === 'ig' && t.linked_fb_page && (
                                  <span className="text-[9px] text-pink-700 bg-pink-50 px-1 rounded">via {t.linked_fb_page.slice(0, 18)}</span>
                                )}
                                {checked && (
                                  <button
                                    type="button"
                                    onClick={() => setExpandedTargetKey(expanded ? null : k)}
                                    className="text-[10px] text-zinc-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50"
                                    data-testid={`target-edit-caption-${platform}-${t.account_id}`}
                                    title={expanded ? 'Hide caption editor' : 'Edit caption for this destination'}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                              {expanded && checked && (
                                <div className="ml-7 mr-2 mb-2 space-y-1.5" data-testid={`target-caption-editor-${platform}-${t.account_id}`}>
                                  <Textarea
                                    rows={3}
                                    placeholder={post?.caption || 'Custom caption for this destination…'}
                                    value={captionOverrides[k] ?? ''}
                                    onChange={(e) =>
                                      setCaptionOverrides((cur) => ({ ...cur, [k]: e.target.value }))
                                    }
                                    className="text-xs"
                                  />
                                  <div className="flex items-center justify-between">
                                    <p className="text-[10px] text-zinc-500">
                                      {captionOverrides[k]?.trim()
                                        ? 'This caption overrides the base for this destination only.'
                                        : 'Leave empty to use the base caption.'}
                                    </p>
                                    {overridden && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setCaptionOverrides((cur) => {
                                            const next = { ...cur };
                                            delete next[k];
                                            return next;
                                          })
                                        }
                                        className="text-[10px] text-zinc-500 hover:text-red-600 inline-flex items-center gap-0.5"
                                      >
                                        <XIcon className="w-2.5 h-2.5" />
                                        Reset
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {selectedKeys.length > 1 && (
                <p className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1">
                  Will create <strong>{selectedKeys.length}</strong> separate scheduled posts — one per destination.
                </p>
              )}
            </div>

            {/* Image generation */}
            <div className="space-y-2 pt-2 border-t border-zinc-200">
              <div className="flex items-center justify-between gap-2">
                <Label className="flex items-center gap-1.5">
                  <ImageIcon className="w-3.5 h-3.5 text-indigo-600" />
                  Post image
                </Label>
                <Select value={imageModel} onValueChange={setImageModel}>
                  <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="image-model-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {IMAGE_MODELS.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        <div className="flex flex-col">
                          <span className="text-xs font-medium">{m.label}</span>
                          <span className="text-[10px] text-zinc-500">{m.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {generatedImage ? (
                <div className="relative rounded-lg overflow-hidden border border-zinc-200 group bg-zinc-50">
                  <img
                    src={`data:image/png;base64,${generatedImage}`}
                    alt="Generated"
                    className="w-full max-h-[420px] object-contain mx-auto bg-zinc-50"
                    data-testid="generated-image-preview"
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={handleGenerateImage}
                    disabled={generatingImage}
                    data-testid="regenerate-image-btn"
                  >
                    {generatingImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleGenerateImage}
                  disabled={generatingImage}
                  data-testid="generate-image-btn"
                >
                  {generatingImage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Generate image with AI
                </Button>
              )}
              {post?.image_prompt && (
                <p className="text-[10px] text-zinc-500 italic line-clamp-2">Prompt: {post.image_prompt}</p>
              )}
            </div>

            {/* Media upload */}
            <div className="space-y-2 pt-2 border-t border-zinc-200">
              <div className="flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5 text-indigo-600" />
                <span className="text-sm font-medium text-zinc-700">Attach media</span>
              </div>
              <PostMediaUpload
                post={livePost}
                onChange={(newMedia) => {
                  const updated = { ...livePost, media: newMedia };
                  setLivePost(updated);
                  onScheduled?.(updated);
                }}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
          {post?.scheduled_for ? (
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={handleUnschedule}
              disabled={unscheduling || saving}
              data-testid="unschedule-btn"
            >
              {unscheduling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Remove from calendar
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handleSchedule}
              disabled={saving}
              data-testid="schedule-confirm-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {post?.scheduled_for ? 'Update' : 'Schedule'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
